/**
 * Keep-list wipe for the Flight Log and the Flight plans board.
 *
 * The dispatcher names the tickets that survive (CEL1003F + CEL1001 by
 * default); every other board ticket, log row, and the assignment that carried
 * it is dropped. Both lists go together because the Flight Log is a projection
 * of the board — clearing one alone re-projects the other straight back.
 *
 * Fail closed: nothing is invented and nothing is matched loosely. An empty
 * keep list clears the whole board rather than silently keeping everything,
 * and keepers that are not on the desk come back as `missingKeep` so the caller
 * can stop instead of guessing.
 */
import {
  displayFlightNumber,
  FIRST_FLIGHT_SEQ,
  hydrateNextFlightSeq,
  normalizeFlightHint,
} from "./flightNumber";
import type { FlightLogEntry, Mission } from "./types";

/** Emerson's desk: the two tickets that existed before the 0.3.36 mint runaway. */
export const DEFAULT_FLIGHT_LOG_KEEP: readonly string[] = ["CEL1003F", "CEL1001"];

export type FlightLogClearPlan = {
  /** Normalized keep list, in the order given. */
  keep: string[];
  keptMissionIds: string[];
  keptLogIds: string[];
  removedMissionIds: string[];
  removedLogIds: string[];
  /** Keepers that match nothing on this desk — caller decides whether to abort. */
  missingKeep: string[];
};

export type FlightLogClearInput = {
  logs: readonly FlightLogEntry[];
  missions: readonly Mission[];
  keep?: string | readonly string[] | null;
};

/** "CEL1003F, cel 1001" / ["CEL1003F"] → ["CEL1003F", "CEL1001"]. */
export function parseFlightLogKeepList(raw: string | readonly string[] | null | undefined): string[] {
  const parts = Array.isArray(raw) ? raw : String(raw ?? "").split(/[,;\n]+/);
  const out: string[] = [];
  for (const part of parts) {
    const key = normalizeFlightHint(part);
    if (key && !out.includes(key)) out.push(key);
  }
  return out;
}

function missionKeys(mission: Mission): string[] {
  return [normalizeFlightHint(displayFlightNumber(mission)), normalizeFlightHint(mission.callsign), mission.id].filter(
    Boolean,
  );
}

function logKeys(log: FlightLogEntry): string[] {
  return [
    normalizeFlightHint(log.flightNumber),
    normalizeFlightHint(log.callsign),
    log.tripId,
    log.id,
  ].filter(Boolean);
}

export function planFlightLogClear(input: FlightLogClearInput): FlightLogClearPlan {
  const keep = parseFlightLogKeepList(input.keep ?? DEFAULT_FLIGHT_LOG_KEEP);
  const keepSet = new Set(keep);
  const matched = new Set<string>();

  const keptMissionIds: string[] = [];
  const removedMissionIds: string[] = [];
  for (const mission of input.missions) {
    const hit = missionKeys(mission).find((key) => keepSet.has(key));
    if (hit) {
      matched.add(hit);
      keptMissionIds.push(mission.id);
    } else {
      removedMissionIds.push(mission.id);
    }
  }

  // A log row survives on its own flight number or because its ticket survived —
  // history (wheels, AAR) must not fall off a keeper.
  const keptTrips = new Set(keptMissionIds);
  const keptLogIds: string[] = [];
  const removedLogIds: string[] = [];
  for (const log of input.logs) {
    const hit = logKeys(log).find((key) => keepSet.has(key));
    if (hit) matched.add(hit);
    if (hit || keptTrips.has(log.tripId)) keptLogIds.push(log.id);
    else removedLogIds.push(log.id);
  }

  return {
    keep,
    keptMissionIds,
    keptLogIds,
    removedMissionIds,
    removedLogIds,
    missingKeep: keep.filter((key) => !matched.has(key)),
  };
}

export type FlightLogClearAssignment = {
  id: string;
  missionId?: string;
  ferryMissionId?: string;
  returnFerryMissionId?: string;
};

export type FlightLogClearResult<TAssignment extends FlightLogClearAssignment> = {
  plan: FlightLogClearPlan;
  flightLogs: FlightLogEntry[];
  missions: Mission[];
  assignments: TAssignment[];
  /** Renumber from the keepers so the next filed trip is not CEL1040-something. */
  nextFlightSeq: number;
};

export function applyFlightLogClear<TAssignment extends FlightLogClearAssignment>(
  input: FlightLogClearInput & { assignments?: readonly TAssignment[] },
): FlightLogClearResult<TAssignment> {
  const plan = planFlightLogClear(input);
  const droppedMissions = new Set(plan.removedMissionIds);
  const droppedLogs = new Set(plan.removedLogIds);
  const missions = input.missions.filter((m) => !droppedMissions.has(m.id));
  const flightLogs = input.logs.filter((l) => !droppedLogs.has(l.id));
  const assignments = (input.assignments ?? [])
    .filter((a) => !(a.missionId && droppedMissions.has(a.missionId)))
    .map((a) => {
      const ferryMissionId = a.ferryMissionId && droppedMissions.has(a.ferryMissionId) ? undefined : a.ferryMissionId;
      const returnFerryMissionId =
        a.returnFerryMissionId && droppedMissions.has(a.returnFerryMissionId) ? undefined : a.returnFerryMissionId;
      if (ferryMissionId === a.ferryMissionId && returnFerryMissionId === a.returnFerryMissionId) return a;
      return { ...a, ferryMissionId, returnFerryMissionId };
    });
  return {
    plan,
    flightLogs,
    missions,
    assignments,
    nextFlightSeq: hydrateNextFlightSeq(FIRST_FLIGHT_SEQ, missions),
  };
}

/** Desk notice / Megan readback — counts first, then who survived. */
export function flightLogClearSummary(plan: FlightLogClearPlan): string {
  const logs = plan.removedLogIds.length;
  const trips = plan.removedMissionIds.length;
  const kept = plan.keep.length ? plan.keep.join(", ") : "nothing";
  const head = `cleared ${logs} log row${logs === 1 ? "" : "s"} and ${trips} flight plan${trips === 1 ? "" : "s"}`;
  const missing = plan.missingKeep.length ? ` · not on the desk: ${plan.missingKeep.join(", ")}` : "";
  return `${head} · kept ${kept}${missing}`;
}

/** Confirm copy before the wipe runs — Megan and the desk button say the same thing. */
export function flightLogClearConfirmCopy(plan: FlightLogClearPlan): string {
  const logs = plan.removedLogIds.length;
  const trips = plan.removedMissionIds.length;
  const kept = plan.keep.length ? plan.keep.join(" and ") : "nothing";
  return `Clear ${logs} flight log row${logs === 1 ? "" : "s"} and ${trips} flight plan${
    trips === 1 ? "" : "s"
  } — keep ${kept}. This cannot be undone.`;
}
